import { defineStore } from "pinia";
import { request, requestBool, requestRaw } from "@/api-connectors/basicRequests";
import { AxiosResponse } from "axios";
import { Duration } from "luxon";

import { useListStore } from "@/stores/lists";
import { usePersistentStore } from "@/stores/persistent";

import { isCustomizedErrorResponse } from "@/functions/handler";
import { isCustomSupplierEnum } from "@/functions/converter";
import { luxonFromNow } from "@/functions/luxon";
import { queryMatches } from "@/functions/others";
import { catalogSort } from "@/functions/sort";

import {
    CatalogFilterAttributes,
    CatalogItem,
    CatalogParams,
    DeliveryDate,
    List,
    LocationSupplier,
    Supplier,
    SupplierCategory,
    SupplierImageInfo,
    SupplierSettings
} from "@/types/supplier";
import { PaginatedList } from "@/types/store";
import { CATALOG_MODE, CUSTOM_SUPPLIER_TYPE, INDUSTRY_TYPE, KIND } from "@/types/enum/supplierEnum";
import { SORT_METHOD } from "@/types/enum/propEnum";
import { InterfaceRecord } from "@/types/helper";
import { nextTick } from "vue";
import { categoryMatches } from "@/functions/piniaFunctions";
import { DELIVERY_TYPE } from "@/types/enum/order";

export interface SupplierStore {
    publicSuppliers: PaginatedList<Supplier>,
    selectedSupplier: Supplier | null,
    suppliers: PaginatedList<Supplier>,
    locationSupplierImages: Record<string, { info: SupplierImageInfo[], mainSupplier: string }>
    supplierCategories: Record<string, SupplierCategory[]>
    catalog: {
        [supplierId: string]: PaginatedList<CatalogItem>
    },
    listCatalog: {
        [supplierId: string]: {
            [listId: string]: PaginatedList<CatalogItem>
        }
    }
    specialOffers: {
        [supplierId: string]: PaginatedList<CatalogItem>
    },
    lastSkip: number,
    sortDesc: SORT_METHOD,
    searchQuery: string,
    catalogFilter: CatalogFilterAttributes;
    itemsLoaded: boolean;
    addedSupplierName?: string;
    itemIndexList: number;
    deliveryDays: Record<string /*supplierId*/, DeliveryDate[]>
    pickupDays: Record<string /*supplierId*/, DeliveryDate[]>
    listCatalogLoaded: boolean;
    isLoaded: InterfaceRecord<SupplierStore, boolean, 'isLoaded' | 'itemsLoaded' | 'listCatalogLoaded'>
}

interface CatalogRequestConditions {
    includeList?: string;
    specialOffersOnly?: boolean;
    supplierId: string;
    response: PaginatedList<CatalogItem>
}

interface CatalogPatchBase {
    target: Record<string, PaginatedList<CatalogItem>> | Record<string, Record<string, PaginatedList<CatalogItem>>>;
    mode: CATALOG_MODE;
    supplierId: string;
    response: PaginatedList<CatalogItem>;
}

interface ListCatalogPatch extends CatalogPatchBase {
    mode: CATALOG_MODE.LIST_CATALOG;
    target: Record<string, Record<string, PaginatedList<CatalogItem>>>;
    listId: string;
}

interface BasicCatalogPatch extends CatalogPatchBase {
    mode: CATALOG_MODE.CATALOG;
    target: Record<string, PaginatedList<CatalogItem>>
}

interface SpecialOffersPatch extends CatalogPatchBase {
    mode: CATALOG_MODE.SPECIAL_OFFERS;
    target: Record<string, PaginatedList<CatalogItem>>
}

type CatalogPatch = BasicCatalogPatch | ListCatalogPatch | SpecialOffersPatch

interface FillCatalogParams {
    catalogMode: CatalogPatch;
    skip?: number;
    end: number;
    take: number;
}

export const useSupplierStore = defineStore('supplier', {
    state: (): SupplierStore => {
        return {
            publicSuppliers: { data: [], count: 0 },
            selectedSupplier: null,
            suppliers: { data: [], count: 0 },
            locationSupplierImages: {},
            supplierCategories: {},
            catalog: {},
            listCatalog: {},
            specialOffers: {},
            lastSkip: 0,
            sortDesc: SORT_METHOD.ALPH_ASC,
            searchQuery: '',
            catalogFilter: {
                category: {},
                sortDirection: SORT_METHOD.ALPH_ASC,
                lastSupplierId: ''
            },
            itemsLoaded: true,
            addedSupplierName: undefined,
            itemIndexList: 0,
            listCatalogLoaded: false,
            deliveryDays: {},
            pickupDays: {},
            isLoaded: {}
        }
    },

    actions: {
        clearStateVariables(): void {
            this.selectedSupplier = null
            this.catalog = {}
        },

        async getPublicSuppliers(): Promise<AxiosResponse | undefined> {
            const response = await requestRaw({
                method: 'GET',
                route: `suppliers`,
                query: { take: 0 }
            })

            if (isCustomizedErrorResponse(response)) return undefined
            this.publicSuppliers = response.data
            return response
        },

        async getSupplier(locationId: string, supplierId: string): Promise<Supplier | undefined> {
            const response = await request<Supplier>({
                method: 'GET',
                route: `locations/${locationId}/suppliers/${supplierId}`,
            })

            if (!response) return undefined

            return response
        },

        isMoreImportantSupplier(newSupplier: Supplier, mainSupplier: Supplier | undefined): boolean {
            if (!mainSupplier) return true
            if (newSupplier.industry === INDUSTRY_TYPE.PRIVATE) return false
            if (mainSupplier.industry === INDUSTRY_TYPE.PRIVATE) return true
            if (newSupplier.industry === INDUSTRY_TYPE.DRINK && mainSupplier.industry !== INDUSTRY_TYPE.DRINK) return true
            return false
        },

        async getLocationSupplierImage(locationId: string): Promise<boolean> {
            const response = await request<PaginatedList<Supplier>>({
                method: 'GET',
                route: `locations/${locationId}/suppliers`,
            })

            if (!response) return false

            let currentMainSupplier;

            for (const supplier of response.data) {
                if (!this.locationSupplierImages[locationId]) this.locationSupplierImages[locationId] = {
                    info: [],
                    mainSupplier: ''
                }
                if (this.isMoreImportantSupplier(supplier, currentMainSupplier)) {
                    currentMainSupplier = supplier
                }
                if (this.locationSupplierImages[locationId]?.info?.find(existingSupplier => existingSupplier.id === supplier.id)) continue
                if (!supplier.imageLink) {
                    this.locationSupplierImages[locationId]?.info?.push({
                        img: supplier.iconType ?? CUSTOM_SUPPLIER_TYPE.OTHER,
                        id: supplier.id,
                        industry: supplier.industry ?? INDUSTRY_TYPE.PRIVATE
                    })
                    continue;
                }
                this.locationSupplierImages[locationId]?.info?.push({
                    img: supplier.imageLink,
                    id: supplier.id,
                    industry: supplier.industry ?? INDUSTRY_TYPE.PRIVATE
                })
            }

            if (this.locationSupplierImages[locationId]) this.locationSupplierImages[locationId].mainSupplier = currentMainSupplier?.id ?? ''

            return true
        },

        async getLocationSupplierOne(locationId: string, supplierId: string): Promise<Supplier | undefined> {
            const response = await request<Supplier>({
                method: 'GET',
                route: `locations/${locationId}/suppliers/${supplierId}`,
            })

            if (!response) return undefined

            const supplierIndex = this.suppliers.data?.findIndex(supplier => supplier.id === supplierId)

            if (supplierIndex > 0) this.suppliers.data[supplierIndex] = response

            return response
        },


        async getCatalog(param: CatalogParams): Promise<PaginatedList<CatalogItem> | undefined> {
            const { supplierId, skip, locationId, specialOffersOnly, includeList, category } = param
            let { take, sortField, searchKey, end } = param

            if (!supplierId) { //TODO: Find cause of missing supplierId on first call
                console.error('Missing SupplierId')
                return
            }
            if (!take && take !== 0) take = 50
            if (!end) end = 50
            if (!sortField) sortField = 'name'
            if (searchKey === undefined) searchKey = this.searchQuery
            if (skip) this.lastSkip += take

            const query: Record<string, any> = {
                take,
                skip,
                sortField,
                sortDesc: (this.catalogFilter.sortDirection === SORT_METHOD.ALPH_DESC)
            }
            if (searchKey) query.searchKey = searchKey
            if (specialOffersOnly) query.specialOffersOnly = specialOffersOnly
            if (includeList) query.includeList = includeList
            if (category) query.category = category

            const response = await request<PaginatedList<CatalogItem>>({
                method: 'GET',
                route: `locations/${locationId}/suppliers/${supplierId}/catalog`,
                query
            }, true)

            if (!response) return undefined

            const catalogMode = await this.evaluateCatalogMode({ specialOffersOnly, includeList, supplierId, response })

            await this.fillCatalogMode({ catalogMode, skip, end, take })

            nextTick(() => window.dispatchEvent(new Event('force-reload-catalog')))
            return response
        },
        async evaluateCatalogMode(params: CatalogRequestConditions): Promise<CatalogPatch> {
            const { supplierId, response } = params
            if (params.includeList) {
                if (!this.listCatalog[supplierId]) this.listCatalog[supplierId] = {}
                return {
                    mode: CATALOG_MODE.LIST_CATALOG,
                    target: this.listCatalog,
                    supplierId,
                    listId: params.includeList,
                    response
                }
            }
            if (params.specialOffersOnly) return {
                mode: CATALOG_MODE.SPECIAL_OFFERS,
                target: this.specialOffers,
                supplierId,
                response
            }
            return {
                mode: CATALOG_MODE.CATALOG,
                target: this.catalog,
                supplierId,
                response
            }
        },

        async fillCatalogMode(params: FillCatalogParams): Promise<void> {
            const { skip, catalogMode, end, take } = params

            if (catalogMode.mode !== CATALOG_MODE.LIST_CATALOG) {
                if (skip) {
                    for (let i = 0; i < take; i++) {
                        if (!catalogMode.response.data[i]) continue

                        catalogMode.target[catalogMode.supplierId].data[(end - take) + i] = catalogMode.response.data[i]
                    }
                } else {
                    if (catalogMode) {
                        if (!catalogMode.target[catalogMode.supplierId]) catalogMode.target[catalogMode.supplierId] = new PaginatedList<CatalogItem>([])
                        catalogMode.target[catalogMode.supplierId].data = []

                        await this.patchCatalog(catalogMode)
                    }
                }
                return
            }

            if (skip) {
                for (let i = 0; i < take; i++) {
                    if (!catalogMode.response.data[i]) continue

                    catalogMode.target[catalogMode.supplierId][catalogMode.listId].data[(end - take) + i] = catalogMode.response.data[i]
                }
            } else {
                if (catalogMode) {
                    if (!catalogMode.target[catalogMode.supplierId][catalogMode.listId]) catalogMode.target[catalogMode.supplierId][catalogMode.listId] = new PaginatedList<CatalogItem>([])
                    catalogMode.target[catalogMode.supplierId][catalogMode.listId].data = []

                    await this.patchCatalog(catalogMode)
                }
            }
        },

        async patchCatalog(params: CatalogPatch): Promise<void> {
            const { target, mode, supplierId, response } = params
            if (mode === CATALOG_MODE.SPECIAL_OFFERS) {
                this.$patch({ specialOffers: { [supplierId]: response } })
                return
            }

            if (mode === CATALOG_MODE.LIST_CATALOG) {
                this.$patch({
                    listCatalog: { [supplierId]: { [params.listId]: response } },
                })
                this.lastSkip = target[supplierId][params.listId].data.length
                this.listCatalogLoaded = true
                return
            }

            this.$patch({ catalog: { [supplierId]: response } })
            this.lastSkip = target[supplierId].data.length
        },

        async refreshCatalog(param: CatalogParams) {
            this.listCatalogLoaded = false
            await this.getCatalog(param)
            this.listCatalogLoaded = true
        },

        async getLocationSupplier(locationId: string): Promise<AxiosResponse | undefined> {
            if (!locationId) return undefined
            const response = await requestRaw({
                method: 'GET',
                route: `locations/${locationId}/suppliers`,
            })

            if (isCustomizedErrorResponse(response)) return undefined
            this.suppliers = response.data

            return response
        },

        async addLocationSupplier(locationId: string, body: LocationSupplier): Promise<Supplier | undefined> {
            const response = await request<Supplier>({
                method: 'POST',
                route: `locations/${locationId}/suppliers`,
                body: body
            }, true)

            if (!response) return undefined

            await this.getLocationSupplier(locationId)
            return response
        },

        async removeLocationSupplier(locationId: string, supplierId: string): Promise<boolean> {
            const response = await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/suppliers/${supplierId}`
            }, true)

            if (!response) return false

            delete this.locationSupplierImages[locationId]

            await this.getLocationSupplier(locationId)
            return true
        },

        async editCustomLocationSupplier(locationId: string, supplierId: string, body: Supplier): Promise<boolean> {
            const response = await requestBool({
                method: 'PATCH',
                route: `locations/${locationId}/suppliers/${supplierId}`,
                body: body
            }, true)

            if (!response) return false

            delete this.locationSupplierImages[locationId]

            await this.getLocationSupplier(locationId)
            return response
        },

        async getSupplierCategories(locationId: string, supplierId: string): Promise<boolean> {
            const response = await request<SupplierCategory[]>({
                method: 'GET',
                route: `locations/${locationId}/suppliers/${supplierId}/categories`
            })

            if (!response) return false

            this.supplierCategories[supplierId] = response
            return true
        },

        async changeSupplierSettings(locationId: string, supplierId: string, settings: SupplierSettings) {
            const response = await requestBool({
                method: 'PUT',
                route: `locations/${locationId}/suppliers/${supplierId}/settings`,
                body: settings
            })

            if (!response) return false

            await this.getLocationSupplierOne(locationId, supplierId)
            return true
        },

        setInList(list: List, itemId: string): void {
            if (!list.id) return

            for (const item of this.listCatalog[list.supplierId][list.id].data) {
                if (itemId === item.id) item.inList = true
            }
        },

        async getLocationDeliveryDays(locationId: string): Promise<void> {
            const listStore = useListStore()

            await Promise.all(listStore.cartLists.map(item => {
                if (item.supplier.kind === KIND.PRIVATE) return
                this.getSupplierDeliveryDays(locationId, item.supplier.id)
                if (item.supplier.pickupLocations?.length) {
                    this.getSupplierPickupDays(locationId, item.supplier.id)
                }
            }))
        },

        async getSupplierDeliveryDays(locationId: string, supplierId: string): Promise<boolean> {
            const response = await request<DeliveryDate[]>({
                method: "GET",
                route: `/locations/${locationId}/suppliers/${supplierId}/deliveryDays`,
                query: {
                    end: luxonFromNow(Duration.fromObject({ month: 6 }), 'iso')
                }
            })

            if (!response) return false

            this.deliveryDays[supplierId] = response
            return true
        },

        async getSupplierPickupDays(locationId: string, supplierId: string): Promise<boolean> {
            const response = await request<DeliveryDate[]>({
                method: "GET",
                route: `/locations/${locationId}/suppliers/${supplierId}/schedule`,
                query: {
                    type: DELIVERY_TYPE.PICKUP,
                    end: luxonFromNow(Duration.fromObject({ month: 3 }), 'iso')
                }
            })

            if (!response) return false

            this.pickupDays[supplierId] = response
            return true
        },

        async setSelectedSupplierFromList(listId: string, locationId: string) {
            const listStore = useListStore()

            const list = listStore.listById(listId)

            if (!list) return false

            const supplier = this.supplierById(list.supplierId)

            if (!supplier) return false

            if (this.selectedSupplierId === supplier.id) return true
            this.selectedSupplier = supplier
            if (!this.listCatalog[supplier.id]) this.itemsLoaded = false
            await this.getCatalog({
                locationId: locationId,
                supplierId: supplier.id,
                includeList: listId
            })
            this.itemsLoaded = true
            return true
        },

        checkResetFilter(lastListSupplierId: string): void {
            if (this.catalogFilter.lastSupplierId === lastListSupplierId) return

            this.catalogFilter.category = {}
            useListStore().listFilter.category = {}
        },

        changeCatalogRow(supplierId: string): void {
            this.selectedSupplier = this.suppliers.data.find((supplier: Supplier) => supplier.id === supplierId) ?? null
            window.dispatchEvent(new Event('force-reload-catalog'))
        },

        async getSupplierCatalogItemByEan(locationId: string, supplierId: string, ean: string): Promise<void | CatalogItem> {
            const response = await request<CatalogItem>({
                method: "GET",
                route: `/locations/${locationId}/suppliers/${supplierId}/catalog/ean/${ean}`,
            })

            if (!response) return

            return response
        },

        resetCatalogFilter(lastSupplierId?: string, resetSort?: boolean) {
            this.catalogFilter.category = {}
            this.catalogFilter.lastSupplierId = lastSupplierId ?? ''
            if (resetSort) this.catalogFilter.sortDirection = SORT_METHOD.ALPH_ASC
        }
    },
    getters: {
        getItemIndexList(state): number {
            return state.itemIndexList
        },

        selectedSupplierId(state): string {
            return state.selectedSupplier?.id ?? ''
        },

        allSuppliers(state): Supplier[] {
            return state.suppliers.data
        },

        allSpecialOfferSupplier(state): Supplier[] {
            return state.suppliers.data.filter((supplier: Supplier) => supplier.hasSpecialOffers) ?? []
        },

        supplierById(state): (supplierId: string) => Supplier | undefined {
            return (supplierId: string): Supplier | undefined => {
                return state.suppliers.data.find((supplier: Supplier) => supplier.id === supplierId)
            }
        },

        isCustomSupplier(): (supplierId: string | undefined) => boolean {
            return (supplierId: string | undefined): boolean => {
                if (!supplierId) return false;

                const supplier = this.supplierById(supplierId)

                if (!supplier) return false

                return supplier.kind === KIND.PRIVATE
            }
        },

        filteredCatalogItems(state): (listId: string) => CatalogItem[] {
            return (listId: string): CatalogItem[] => {
                const supplierId = useListStore().singleList(listId)?.supplierId ?? ''
                let catalogData: CatalogItem[] = []
                catalogData = listId ? state.listCatalog[supplierId][listId]?.data : state.catalog[supplierId]?.data

                if (!catalogData) return []
                if (!usePersistentStore().offlineMode) return catalogData

                const searchedItems: CatalogItem[] = []

                if (this.searchQuery) {
                    for (const item of catalogData) {
                        if (queryMatches(item, this.searchQuery.toLowerCase())) searchedItems.push(item)
                    }
                } else {
                    Object.assign(searchedItems, catalogData)
                }

                if (useSupplierStore().activeCategoriesNames.length === 0) {
                    return catalogSort(state.sortDesc, searchedItems)
                }

                const filterCatItems: CatalogItem[] = []

                if (!useSupplierStore().selectedSupplier) {
                    const currentSupplier = useSupplierStore().supplierById(supplierId)

                    if (currentSupplier) useSupplierStore().selectedSupplier = currentSupplier
                }

                for (const item of searchedItems) {
                    if (categoryMatches(item, this.activeCategoriesNames)) filterCatItems.push(item)
                }

                return catalogSort(state.sortDesc, filterCatItems)
            }
        },

        getFirstSupplierImage(state): (locationId: string) => SupplierImageInfo {
            return (locationId: string) => {
                const imageInfos: SupplierImageInfo[] = state.locationSupplierImages[locationId]?.info
                if (!imageInfos) return { img: '', id: '', industry: INDUSTRY_TYPE.PRIVATE }

                const mainSupplier = state.locationSupplierImages[locationId]?.mainSupplier
                let imageInfo: SupplierImageInfo;

                if (!mainSupplier) {
                    imageInfo = imageInfos[0]
                } else {
                    imageInfo = imageInfos?.find(imageInfo => imageInfo.id === mainSupplier) ?? imageInfos[0]
                }

                if (!isCustomSupplierEnum(imageInfo.img)) imageInfo.img = imageInfo.img.startsWith('/') ? `https://app.octopusorder.com${imageInfo.img}` : imageInfo.img
                return imageInfo
            }
        },

        getOtherSupplierImages(state): (locationId: string) => SupplierImageInfo[] {
            return (locationId: string): SupplierImageInfo[] => {
                const images: SupplierImageInfo[] = state.locationSupplierImages[locationId]?.info?.filter(info => info.id !== state.locationSupplierImages[locationId]?.mainSupplier)

                if (!images || images.length === 0) return []


                const imagePath: SupplierImageInfo[] = []
                images.forEach(info => {
                    imagePath.push({
                        id: info.id,
                        img: info.img.startsWith('/') ? `https://app.octopusorder.com${info.img}` : info.img,
                        industry: info.industry
                    })
                })
                return imagePath
            }
        },

        getAddedSupplierName(state) {
            return state.addedSupplierName ?? 'den Lieferant'
        },

        getSupplierCategoriesOptions(state): (supplierId?: string) => SupplierCategory[] {
            return (supplierId?: string): SupplierCategory[] => {
                if (!supplierId) return []
                return state.supplierCategories[supplierId] ?? []
            }
        },

        getSupplierCategoryName(state): (categoryId: string, supplierId?: string) => string {
            return (categoryId: string, supplierId?: string): string => {
                if (!supplierId) return ''

                const supplierCategories = state.supplierCategories[supplierId]
                if (!supplierCategories) return ''

                const category = supplierCategories.find(categoryEl => categoryEl.id === categoryId)

                if (!category) return ''
                return category.name
            }
        },
        activeCategoriesIds(state): string[] {
            const categoryIds = Object.keys(state.catalogFilter.category)

            const activeCategoryIds: string[] = []
            for (const categoryId of categoryIds) {
                if (state.catalogFilter.category[categoryId]) activeCategoryIds.push(categoryId)
            }

            return activeCategoryIds
        },

        activeCategoriesNames(state): string[] {
            return this.activeCategoriesIds.map((id) => {
                return this.getSupplierCategoryName(id, state.selectedSupplier?.id)
            })
        },
    }
})
